import { useState } from 'react';
import { bt } from '../i18n';
import { useBuilderStore } from '../state/builderStore';
import { reconcileDiscardAndReload, reconcileKeepMine } from '../state/autosave';
import { pushToast } from '../ui/toastStore';
import { Modal } from '../ui/Modal';

/**
 * 409 conflict reconcile dialog (build-plan §5.5, D7) — the NON-DESTRUCTIVE handling of a stale
 * `@Version` (the page was saved from another tab/device). It is a custom in-app modal (NO native
 * popup) that mounts whenever `saveState === 'conflict'` and forces the seller to choose, never
 * silently reloading and never silently clobbering:
 *
 *  - "Keep my changes" → re-GET the latest version, then re-PUT the in-memory document = deliberate
 *    last-writer-wins by the seller's explicit choice (we do NOT auto-merge a section tree in v1).
 *  - "Discard & reload" → re-GET the server document, replace the store, reset history — the seller
 *    explicitly throws away local unsaved edits.
 *
 * Both surface BOTH outcomes and let the seller decide; neither path loses work without consent.
 */
export function ConflictDialog() {
  const isConflict = useBuilderStore((s) => s.saveState === 'conflict');
  const [busy, setBusy] = useState<null | 'keep' | 'discard'>(null);

  if (!isConflict) return null;

  async function keepMine() {
    setBusy('keep');
    const ok = await reconcileKeepMine();
    setBusy(null);
    if (ok)
      pushToast(
        bt('chrome.conflict.kept', 'Your changes were saved over the other version.'),
        'success',
      );
    else
      pushToast(
        bt('chrome.conflict.keep_failed', 'Could not save your changes. Please try again.'),
        'error',
      );
  }

  async function discard() {
    setBusy('discard');
    const ok = await reconcileDiscardAndReload();
    setBusy(null);
    if (ok)
      pushToast(
        bt('chrome.conflict.reloaded', 'Reloaded the latest version of this page.'),
        'info',
      );
    else
      pushToast(
        bt('chrome.conflict.reload_failed', 'Could not reload this page. Please try again.'),
        'error',
      );
  }

  return (
    <Modal titleId="bld-conflict-title" dismissible={false}>
      <h2 id="bld-conflict-title" className="bld-modal__title">
        {bt('chrome.conflict.title', 'This page changed elsewhere')}
      </h2>
      <p className="bld-modal__body">
        {bt(
          'chrome.conflict.body',
          'Someone (or another tab) saved this page after you opened it. To avoid overwriting that work by accident, choose how to continue:',
        )}
      </p>
      <ul className="bld-modal__list">
        <li>
          <strong>{bt('chrome.conflict.keep', 'Keep my changes')}</strong>{' '}
          {bt(
            'chrome.conflict.keep_explain',
            'overwrites the other version with what you have here now.',
          )}
        </li>
        <li>
          <strong>{bt('chrome.conflict.discard', 'Discard & reload')}</strong>{' '}
          {bt(
            'chrome.conflict.discard_explain',
            'loads the latest saved version and drops your unsaved edits.',
          )}
        </li>
      </ul>
      <div className="bld-modal__actions">
        <button
          type="button"
          className="bld-btn bld-btn--ghost"
          disabled={busy !== null}
          onClick={() => void discard()}
        >
          {busy === 'discard'
            ? bt('chrome.conflict.reloading', 'Reloading…')
            : bt('chrome.conflict.discard', 'Discard & reload')}
        </button>
        <button
          type="button"
          className="bld-btn bld-btn--primary"
          disabled={busy !== null}
          onClick={() => void keepMine()}
        >
          {busy === 'keep'
            ? bt('chrome.common.saving', 'Saving…')
            : bt('chrome.conflict.keep', 'Keep my changes')}
        </button>
      </div>
    </Modal>
  );
}
